import { useEffect, useMemo, useRef, useState } from 'react';
import type { SoulIdentity, SoulIdentityScope } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { useCharacter } from '../hooks/useCharacter.js';
import { useJobs } from '../hooks/useJobs.js';
import { useUI } from '../state/ui.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';
import { Model3DViewer } from './Model3DViewer.js';

type CharacterTab = 'soul-id' | 'character' | 'soul-cast' | 'ai-influencer';

interface SoulPhoto {
  id: string;
  name: string;
  url: string;
  file: File;
}

function splitLines(value: string): string[] {
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function CharacterScreen() {
  const { t } = useI18n();
  const { toast } = useUI();
  const {
    soulIdentities,
    characters,
    aiInfluencers,
    loading,
    createSoulIdentity,
    uploadSoulPhoto,
    startModel3d,
    createCharacter,
    updateCharacter,
    deleteCharacter,
    createAiInfluencer,
    updateAiInfluencer,
    deleteAiInfluencer,
    createSoulCast,
  } = useCharacter();
  const { jobs } = useJobs(true);
  const [tab, setTab] = useState<CharacterTab>('soul-id');
  const [soulName, setSoulName] = useState('');
  const [soulScope, setSoulScope] = useState<SoulIdentityScope>('full-body');
  const [model3dBusyId, setModel3dBusyId] = useState<string | null>(null);
  const [viewing3d, setViewing3d] = useState<SoulIdentity | null>(null);
  const [soulPhotos, setSoulPhotos] = useState<SoulPhoto[]>([]);
  const [soulSubmitting, setSoulSubmitting] = useState(false);
  const [isPhotoDragActive, setIsPhotoDragActive] = useState(false);
  const [characterEditingId, setCharacterEditingId] = useState<string | null>(null);
  const [characterName, setCharacterName] = useState('');
  const [characterDescription, setCharacterDescription] = useState('');
  const [characterSoulId, setCharacterSoulId] = useState('');
  const [characterReferences, setCharacterReferences] = useState('');
  const [castSoulId, setCastSoulId] = useState('');
  const [castCharacterId, setCastCharacterId] = useState('');
  const [castPrompt, setCastPrompt] = useState('');
  const [castTarget, setCastTarget] = useState<'image' | 'video'>('image');
  const [castStyle, setCastStyle] = useState('');
  const [castScene, setCastScene] = useState('');
  const [influencerEditingId, setInfluencerEditingId] = useState<string | null>(null);
  const [influencerName, setInfluencerName] = useState('');
  const [influencerBio, setInfluencerBio] = useState('');
  const [influencerSoulId, setInfluencerSoulId] = useState('');
  const [influencerCharacterId, setInfluencerCharacterId] = useState('');
  const [influencerStyle, setInfluencerStyle] = useState('');
  const soulPhotoInputRef = useRef<HTMLInputElement>(null);
  const soulPhotosRef = useRef<SoulPhoto[]>([]);

  const soulCastJobs = useMemo(
    () =>
      jobs.filter((job) => {
        const input = job.input as Record<string, unknown>;
        return input.section === 'character' && input.feature === 'soul-cast';
      }),
    [jobs],
  );
  const statusLabels = {
    training: t('character.status.training'),
    ready: t('character.status.ready'),
    failed: t('character.status.failed'),
  } as const;
  const jobStatusLabels = {
    pending: t('job.pending'),
    processing: t('job.processing'),
    done: t('job.done'),
    failed: t('job.failed'),
    cancelled: t('job.cancelled'),
  } as const;

  useEffect(() => {
    soulPhotosRef.current = soulPhotos;
  }, [soulPhotos]);

  useEffect(
    () => () => {
      soulPhotosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
    },
    [],
  );

  function appendSoulPhotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    const nextPhotos = Array.from(files).map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${index}-${Math.random().toString(36).slice(2)}`,
      name: file.name,
      url: URL.createObjectURL(file),
      file,
    }));
    setSoulPhotos((prev) => {
      const combined = [...prev, ...nextPhotos];
      const kept = combined.slice(0, 20);
      combined.slice(20).forEach((photo) => URL.revokeObjectURL(photo.url));
      return kept;
    });
  }

  function clearSoulPhotos() {
    setSoulPhotos((prev) => {
      prev.forEach((photo) => URL.revokeObjectURL(photo.url));
      return [];
    });
  }

  function removeSoulPhoto(id: string) {
    setSoulPhotos((prev) => {
      const target = prev.find((photo) => photo.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((photo) => photo.id !== id);
    });
  }

  async function handleSoulIdentitySubmit() {
    if (!soulName.trim() || soulPhotos.length < 5 || soulPhotos.length > 20) {
      toast(t('character.soulIdPhotoCount'), 'error');
      return;
    }
    setSoulSubmitting(true);
    try {
      const photoUrls: string[] = [];
      for (const photo of soulPhotos) photoUrls.push(await uploadSoulPhoto(photo.file));
      await createSoulIdentity({ name: soulName.trim(), scope: soulScope, photoUrls });
    } catch (error) {
      toast(error instanceof Error ? error.message : t('character.soulIdUploadFailed'), 'error');
      return;
    } finally {
      setSoulSubmitting(false);
    }
    setSoulName('');
    clearSoulPhotos();
  }

  async function handleStartModel3d(id: string) {
    setModel3dBusyId(id);
    try {
      await startModel3d(id);
      toast(t('character.model3dStarted'), 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : t('character.model3dFailed'), 'error');
    } finally {
      setModel3dBusyId(null);
    }
  }

  async function handleCharacterSubmit() {
    const payload = {
      name: characterName.trim(),
      description: characterDescription.trim() || null,
      soulId: characterSoulId || null,
      referenceImageUrls: splitLines(characterReferences),
    };
    if (!payload.name) return;
    if (characterEditingId) {
      await updateCharacter(characterEditingId, payload);
    } else {
      await createCharacter(payload);
    }
    setCharacterEditingId(null);
    setCharacterName('');
    setCharacterDescription('');
    setCharacterSoulId('');
    setCharacterReferences('');
  }

  async function handleInfluencerSubmit() {
    const payload = {
      name: influencerName.trim(),
      bio: influencerBio.trim() || null,
      soulId: influencerSoulId || null,
      characterId: influencerCharacterId || null,
      style: influencerStyle.trim() || null,
    };
    if (!payload.name) return;
    if (influencerEditingId) {
      await updateAiInfluencer(influencerEditingId, payload);
    } else {
      await createAiInfluencer(payload);
    }
    setInfluencerEditingId(null);
    setInfluencerName('');
    setInfluencerBio('');
    setInfluencerSoulId('');
    setInfluencerCharacterId('');
    setInfluencerStyle('');
  }

  async function handleSoulCastSubmit() {
    if (!castPrompt.trim() || (!castSoulId && !castCharacterId)) return;
    await createSoulCast({
      soulId: castSoulId || undefined,
      characterId: castCharacterId || undefined,
      prompt: castPrompt.trim(),
      targetModality: castTarget,
      style: castStyle.trim() || undefined,
      scene: castScene.trim() || undefined,
    });
    setCastPrompt('');
  }

  if (loading) {
    return (
      <div className="jobs-loading">
        <span className="spinner" />
      </div>
    );
  }

  return (
    <section className="media-job-screen">
      <div className="character-tabs" role="tablist" aria-label={t('character.soulIdTitle')}>
        {(
          [
            ['soul-id', t('character.tabSoulId')],
            ['character', t('character.tabCharacter')],
            ['soul-cast', t('character.tabSoulCast')],
            ['ai-influencer', t('character.tabAiInfluencer')],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`btn character-tab-btn ${tab === id ? 'btn-primary is-active' : 'btn-ghost'}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'soul-id' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.soulIdTitle')}</h2>
          <div className="character-form-grid">
            <input
              className="field character-input"
              value={soulName}
              onChange={(event) => setSoulName(event.target.value)}
              placeholder={t('character.name')}
            />
          </div>
          <div className="character-scope" role="radiogroup" aria-label={t('character.scopeLabel')}>
            {(
              [
                ['full-body', t('character.scopeFullBody')],
                ['head', t('character.scopeHead')],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={soulScope === value}
                className={`character-scope-option${soulScope === value ? ' is-active' : ''}`}
                onClick={() => setSoulScope(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="settings-hint">
            {soulScope === 'head' ? t('character.scopeHeadHint') : t('character.scopeFullBodyHint')}
          </p>
          <input
            ref={soulPhotoInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(event) => {
              appendSoulPhotos(event.target.files);
              event.target.value = '';
            }}
          />
          <div
            className={`video-upload-card character-upload-card${isPhotoDragActive ? ' is-drag-active' : ''}`}
            role="button"
            tabIndex={0}
            onDragOver={(event) => {
              event.preventDefault();
              setIsPhotoDragActive(true);
            }}
            onDragLeave={() => setIsPhotoDragActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsPhotoDragActive(false);
              appendSoulPhotos(event.dataTransfer.files);
            }}
            onClick={() => soulPhotoInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              soulPhotoInputRef.current?.click();
            }}
          >
            <span className="video-label">{t('character.soulIdTitle')}</span>
            <span className="video-helper">{t('character.soulIdPhotoHint')}</span>
            <span className="character-upload-count">{soulPhotos.length}/20</span>
            {soulPhotos.length > 0 && (
              <ul className="character-photo-grid" onClick={(event) => event.stopPropagation()}>
                {soulPhotos.map((photo) => (
                  <li key={photo.id} className="character-photo-item">
                    <img src={photo.url} alt={photo.name} className="character-photo-thumb" />
                    <button
                      type="button"
                      className="image-attachment-remove"
                      aria-label={t('common.delete')}
                      onClick={() => removeSoulPhoto(photo.id)}
                    >
                      <Icon name="close" size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={soulSubmitting}
            onClick={() => void handleSoulIdentitySubmit()}
          >
            {t('character.createSoulId')}
          </button>
          <div className="character-list">
            {soulIdentities.map((item) => (
              <div key={item.id} className="job-item character-card">
                <div className="character-card-head">
                  <div className="character-card-identity">
                    <div className="character-card-avatar">
                      {item.photoUrls[0] ? (
                        <img
                          src={item.photoUrls[0]}
                          alt={item.name}
                          className="character-card-thumb"
                        />
                      ) : (
                        <Icon name="userRound" size={16} />
                      )}
                    </div>
                    <strong>{item.name}</strong>
                  </div>
                  <span className={`character-status-badge is-${item.status}`}>
                    {statusLabels[item.status]}
                  </span>
                </div>
                <span className="character-scope-badge">
                  {item.scope === 'head'
                    ? t('character.scopeBadgeHead')
                    : t('character.scopeBadgeFullBody')}
                </span>
                {item.status === 'ready' && (
                  <div className="character-model3d">
                    {item.model3dStatus === 'processing' && (
                      <p className="character-model3d-status" role="status">
                        <span className="spinner" />
                        {t('character.model3dBuilding')}
                      </p>
                    )}
                    {item.model3dStatus === 'failed' && (
                      <p className="character-model3d-error" role="alert">
                        {t('character.model3dFailed')}
                        {item.model3dError ? `: ${item.model3dError}` : ''}
                      </p>
                    )}
                    <div className="character-card-actions">
                      {item.model3dStatus === 'ready' && item.model3dUrl ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setViewing3d(item)}
                        >
                          <Icon name="cube" size={14} />
                          {t('character.model3dView')}
                        </button>
                      ) : null}
                      {(item.model3dStatus === 'none' || item.model3dStatus === 'failed') && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={model3dBusyId === item.id}
                          onClick={() => void handleStartModel3d(item.id)}
                        >
                          <Icon name="cube" size={14} />
                          {item.model3dStatus === 'failed'
                            ? t('character.model3dRetry')
                            : t('character.model3dCreate')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={Boolean(viewing3d)}
        onClose={() => setViewing3d(null)}
        title={viewing3d?.name}
        closeLabel={t('common.close')}
        className="model3d-modal"
      >
        {viewing3d?.model3dUrl ? <Model3DViewer key={viewing3d.id} url={viewing3d.model3dUrl} /> : null}
      </Modal>

      {tab === 'character' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.characterTitle')}</h2>
          <div className="character-form-grid">
            <input
              className="field character-input"
              value={characterName}
              onChange={(event) => setCharacterName(event.target.value)}
              placeholder={t('character.name')}
            />
            <textarea
              className="field character-textarea"
              value={characterDescription}
              onChange={(event) => setCharacterDescription(event.target.value)}
              placeholder={t('character.description')}
            />
            <select
              className="field character-select"
              value={characterSoulId}
              onChange={(event) => setCharacterSoulId(event.target.value)}
            >
              <option value="">{t('character.noSoulLinked')}</option>
              {soulIdentities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <div className="character-reference-field">
              <textarea
                className="field character-textarea"
                value={characterReferences}
                onChange={(event) => setCharacterReferences(event.target.value)}
                placeholder={t('character.referenceImages')}
              />
              <span className="settings-hint">{t('character.referenceImages')}</span>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleCharacterSubmit()}
          >
            {characterEditingId ? t('common.save') : t('character.createCharacter')}
          </button>
          <div className="character-list">
            {characters.map((item) => (
              <div key={item.id} className="job-item character-card">
                <div className="character-card-head">
                  <div className="character-card-identity">
                    <div className="character-card-avatar">
                      {item.referenceImageUrls[0] ? (
                        <img
                          src={item.referenceImageUrls[0]}
                          alt={item.name}
                          className="character-card-thumb"
                        />
                      ) : (
                        <Icon name="image" size={16} />
                      )}
                    </div>
                    <strong>{item.name}</strong>
                  </div>
                </div>
                {item.description ? (
                  <p className="character-card-copy">{item.description}</p>
                ) : null}
                <div className="character-card-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setCharacterEditingId(item.id);
                      setCharacterName(item.name);
                      setCharacterDescription(item.description ?? '');
                      setCharacterSoulId(item.soulId ?? '');
                      setCharacterReferences(item.referenceImageUrls.join('\n'));
                    }}
                  >
                    <Icon name="edit" size={14} />
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void deleteCharacter(item.id)}
                  >
                    <Icon name="trash" size={14} />
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'soul-cast' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.soulCastTitle')}</h2>
          <div className="character-form-grid">
            <select
              className="field character-select"
              value={castSoulId}
              onChange={(event) => setCastSoulId(event.target.value)}
            >
              <option value="">{t('character.pickSoulId')}</option>
              {soulIdentities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <select
              className="field character-select"
              value={castCharacterId}
              onChange={(event) => setCastCharacterId(event.target.value)}
            >
              <option value="">{t('character.pickCharacter')}</option>
              {characters.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <textarea
              className="field character-textarea"
              value={castPrompt}
              onChange={(event) => setCastPrompt(event.target.value)}
              placeholder={t('character.prompt')}
            />
            <select
              className="field character-select"
              value={castTarget}
              onChange={(event) => setCastTarget(event.target.value as 'image' | 'video')}
            >
              <option value="image">{t('section.image')}</option>
              <option value="video">{t('section.video')}</option>
            </select>
            <input
              className="field character-input"
              value={castStyle}
              onChange={(event) => setCastStyle(event.target.value)}
              placeholder={t('character.style')}
            />
            <input
              className="field character-input"
              value={castScene}
              onChange={(event) => setCastScene(event.target.value)}
              placeholder={t('character.scene')}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleSoulCastSubmit()}
          >
            {t('character.generateSoulCast')}
          </button>
          <div className="character-list">
            {soulCastJobs.map((job) => (
              <div key={job.id} className={`job-item character-card job-${job.status}`}>
                <div className="character-card-head">
                  <strong>{job.modelId}</strong>
                  <span className={`character-status-badge is-job-${job.status}`}>
                    {jobStatusLabels[job.status]}
                  </span>
                </div>
                <p className="character-card-copy">
                  {String((job.input as Record<string, unknown>).prompt ?? '')}
                </p>
                {job.resultUrl ? (
                  <a
                    className="btn btn-ghost character-result-link"
                    href={job.resultUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="external" size={14} />
                    {t('job.result')}
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'ai-influencer' && (
        <div className="settings-card character-card-surface">
          <h2>{t('character.aiInfluencerTitle')}</h2>
          <div className="character-form-grid">
            <input
              className="field character-input"
              value={influencerName}
              onChange={(event) => setInfluencerName(event.target.value)}
              placeholder={t('character.name')}
            />
            <textarea
              className="field character-textarea"
              value={influencerBio}
              onChange={(event) => setInfluencerBio(event.target.value)}
              placeholder={t('character.bio')}
            />
            <select
              className="field character-select"
              value={influencerSoulId}
              onChange={(event) => setInfluencerSoulId(event.target.value)}
            >
              <option value="">{t('character.pickSoulId')}</option>
              {soulIdentities.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <select
              className="field character-select"
              value={influencerCharacterId}
              onChange={(event) => setInfluencerCharacterId(event.target.value)}
            >
              <option value="">{t('character.pickCharacter')}</option>
              {characters.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <input
              className="field character-input"
              value={influencerStyle}
              onChange={(event) => setInfluencerStyle(event.target.value)}
              placeholder={t('character.style')}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleInfluencerSubmit()}
          >
            {influencerEditingId ? t('common.save') : t('character.createAiInfluencer')}
          </button>
          <div className="character-list">
            {aiInfluencers.map((item) => (
              <div key={item.id} className="job-item character-card">
                <div className="character-card-head">
                  <div className="character-card-identity">
                    <div className="character-card-avatar">
                      <Icon name="star" size={16} />
                    </div>
                    <strong>{item.name}</strong>
                  </div>
                </div>
                {item.bio ? <p className="character-card-copy">{item.bio}</p> : null}
                <div className="character-card-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setInfluencerEditingId(item.id);
                      setInfluencerName(item.name);
                      setInfluencerBio(item.bio ?? '');
                      setInfluencerSoulId(item.soulId ?? '');
                      setInfluencerCharacterId(item.characterId ?? '');
                      setInfluencerStyle(item.style ?? '');
                    }}
                  >
                    <Icon name="edit" size={14} />
                    {t('common.save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setTab('soul-cast');
                      setCastSoulId(item.soulId ?? '');
                      setCastCharacterId(item.characterId ?? '');
                      setCastStyle(item.style ?? '');
                    }}
                  >
                    <Icon name="send" size={14} />
                    {t('character.generateContent')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void deleteAiInfluencer(item.id)}
                  >
                    <Icon name="trash" size={14} />
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
